import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useAuth } from "../../contexts/auth/Context";
import { useToast } from "../../contexts/toast/Context";
import {
  getAppBannerReviewsRequest,
  approveAppBannerRequest,
  rejectAppBannerRequest,
} from "../requests/adminAppBanners";
import {
  ADMIN_APP_BANNERS_QUERY_KEY,
  adminAppBannersQueryKey,
} from "../../query/keys";
import { CACHE_TIMES } from "../../query/options";
import type { AppBannerReviewFilter } from "../../utils/zod/adminAppBanners";

export const APP_BANNER_REVIEWS_PAGE_SIZE = 10;

export const useAppBannerReviews = (
  status: AppBannerReviewFilter,
  page: number,
) => {
  const queryClient = useQueryClient();
  const { token } = useAuth();
  const { showToast } = useToast();

  const { data, isLoading, isError } = useQuery({
    queryKey: adminAppBannersQueryKey(status, page),
    queryFn: () =>
      getAppBannerReviewsRequest(
        { status, page, limit: APP_BANNER_REVIEWS_PAGE_SIZE },
        token!,
      ),
    enabled: !!token,
    // La lista no debe colapsar a vacío mientras se cambia de página o filtro.
    placeholderData: keepPreviousData,
    ...CACHE_TIMES.TRANSACTIONAL_DATA,
  });

  // Una decisión mueve el banner de un filtro a otro y cambia el contador de
  // pendientes: se invalida toda la rama.
  const onSettled = (message: string) => () => {
    queryClient.invalidateQueries({ queryKey: ADMIN_APP_BANNERS_QUERY_KEY });
    showToast(message, "success");
  };

  const approve = useMutation({
    mutationFn: (bannerPublicId: string) =>
      approveAppBannerRequest(bannerPublicId, token!),
    onSuccess: onSettled("Banner aprobado"),
    onError: (error: Error) => {
      queryClient.invalidateQueries({ queryKey: ADMIN_APP_BANNERS_QUERY_KEY });
      showToast(error.message || "Error al aprobar el banner", "error");
    },
  });

  const reject = useMutation({
    mutationFn: ({
      bannerPublicId,
      reason,
    }: {
      bannerPublicId: string;
      reason: string;
    }) => rejectAppBannerRequest(bannerPublicId, reason, token!),
    onSuccess: onSettled("Banner rechazado"),
    onError: (error: Error) => {
      queryClient.invalidateQueries({ queryKey: ADMIN_APP_BANNERS_QUERY_KEY });
      showToast(error.message || "Error al rechazar el banner", "error");
    },
  });

  return {
    items: data?.data ?? [],
    meta: data?.meta,
    isLoading,
    isError,
    approveBanner: approve.mutate,
    rejectBanner: reject.mutateAsync,
    isResolving: approve.isPending || reject.isPending,
  };
};

/** Contador de pendientes para el menú: pide una sola fila y lee el total. */
export const usePendingAppBannersCount = (enabled: boolean) => {
  const { token } = useAuth();

  const { data } = useQuery({
    queryKey: adminAppBannersQueryKey("PENDING", 0),
    queryFn: () =>
      getAppBannerReviewsRequest(
        { status: "PENDING", page: 1, limit: 1 },
        token!,
      ),
    enabled: enabled && !!token,
    ...CACHE_TIMES.TRANSACTIONAL_DATA,
  });

  return data?.meta.totalCount ?? 0;
};
